/**
 * Event Store
 *
 * Low-level event state for the unified event system.
 * Tracks connection status, recent events (for debugging),
 * and channel subscriptions.
 */

import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { UnifiedEvent, EventChannel } from "@/types/events.types";

// ============================================================
// Types
// ============================================================

interface EventState {
  connectionStatus:
    "disconnected" | "connecting" | "connected" | "reconnecting";
  lastConnectedAt: Date | null;
  reconnectAttempts: number;
  recentEvents: UnifiedEvent[];
  maxRecentEvents: number;
  activeChannels: EventChannel[];
}

interface EventStore extends EventState {
  setConnectionStatus: (status: EventState["connectionStatus"]) => void;
  recordEvent: (event: UnifiedEvent) => void;
  clearEvents: () => void;
  subscribeToChannel: (channel: EventChannel) => void;
  unsubscribeFromChannel: (channel: EventChannel) => void;
  incrementReconnectAttempts: () => void;
  resetReconnectAttempts: () => void;
}

// ============================================================
// Initial State
// ============================================================

const initialState: EventState = {
  connectionStatus: "disconnected",
  lastConnectedAt: null,
  reconnectAttempts: 0,
  recentEvents: [],
  maxRecentEvents: 100,
  activeChannels: ["execution", "notifications", "assets", "system"],
};

// ============================================================
// Store
// ============================================================

export const useEventStore = create<EventStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setConnectionStatus: (status) => {
        set(
          {
            connectionStatus: status,
            ...(status === "connected" ? { lastConnectedAt: new Date() } : {}),
          },
          false,
          "setConnectionStatus",
        );
      },

      recordEvent: (event) => {
        const { recentEvents, maxRecentEvents } = get();
        const updated = [event, ...recentEvents];
        set(
          {
            recentEvents:
              updated.length > maxRecentEvents
                ? updated.slice(0, maxRecentEvents)
                : updated,
          },
          false,
          "recordEvent",
        );
      },

      clearEvents: () => {
        set({ recentEvents: [] }, false, "clearEvents");
      },

      subscribeToChannel: (channel) => {
        const { activeChannels } = get();
        if (!activeChannels.includes(channel)) {
          set(
            { activeChannels: [...activeChannels, channel] },
            false,
            "subscribeToChannel",
          );
        }
      },

      unsubscribeFromChannel: (channel) => {
        const { activeChannels } = get();
        set(
          { activeChannels: activeChannels.filter((c) => c !== channel) },
          false,
          "unsubscribeFromChannel",
        );
      },

      incrementReconnectAttempts: () => {
        const { reconnectAttempts } = get();
        set(
          { reconnectAttempts: reconnectAttempts + 1 },
          false,
          "incrementReconnectAttempts",
        );
      },

      resetReconnectAttempts: () => {
        set({ reconnectAttempts: 0 }, false, "resetReconnectAttempts");
      },
    }),
    {
      name: "event-store",
      enabled: import.meta.env.DEV,
    },
  ),
);
